import React, { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Zap, Flame, Smartphone, Wifi, Droplet, Wrench, Plus, Pencil, Trash2, Check, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  electric: { label: "Electric", Icon: Zap, bg: "bg-amber-100 dark:bg-amber-500/20", fg: "text-amber-600 dark:text-amber-400" },
  gas: { label: "Gas", Icon: Flame, bg: "bg-orange-100 dark:bg-orange-500/20", fg: "text-orange-600 dark:text-orange-400" },
  phone: { label: "Phone", Icon: Smartphone, bg: "bg-violet-100 dark:bg-violet-500/20", fg: "text-violet-600 dark:text-violet-400" },
  internet: { label: "Internet", Icon: Wifi, bg: "bg-sky-100 dark:bg-sky-500/20", fg: "text-sky-600 dark:text-sky-400" },
  water: { label: "Water", Icon: Droplet, bg: "bg-cyan-100 dark:bg-cyan-500/20", fg: "text-cyan-600 dark:text-cyan-400" },
  other: { label: "Other", Icon: Wrench, bg: "bg-slate-100 dark:bg-slate-800", fg: "text-slate-600 dark:text-slate-300" },
};

const defaultForm = {
  name: "",
  category: "electric",
  amount: "",
  due_day_of_month: "",
  frequency: "monthly",
  due_month: "",
  notes: "",
};

const CYCLE_LABELS = { monthly: "month", quarterly: "quarter", annually: "year" };
const cycleWord = (f) => CYCLE_LABELS[f] || "cycle";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FREQ_LABELS = { monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" };


const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function Bills() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [data, setData] = useState({ bills: [], totals: null });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [historyBill, setHistoryBill] = useState(null);
  const [payBill, setPayBill] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const SORT_KEY = "argent-bills-sort";
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(SORT_KEY) || "default");
  const setAndSaveSort = (v) => { setSortBy(v); try { localStorage.setItem(SORT_KEY, v); } catch {} };

  const load = async () => {
    try {
      const r = await api.get("/bills");
      setData(r.data);
    } catch { toast.error("Couldn't load bills"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(defaultForm);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (b) => {
    setForm({
      name: b.name || "",
      category: b.category || "electric",
      amount: String(b.amount || ""),
      due_day_of_month: String(b.due_day_of_month || ""),
      frequency: b.frequency || "monthly",
      due_month: String(b.due_month || ""),
      notes: b.notes || "",
    });
    setEditingId(b.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give it a name"); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    if (!form.due_day_of_month) { toast.error("Enter the due day of month"); return; }
    if (form.frequency !== "monthly" && !form.due_month) {
      toast.error(`Pick a due month for the ${form.frequency} bill`);
      return;
    }
    try {
      const body = {
        name: form.name.trim(),
        category: form.category,
        amount: parseFloat(form.amount),
        due_day_of_month: parseInt(form.due_day_of_month, 10),
        frequency: form.frequency || "monthly",
        notes: form.notes.trim(),
      };
      if (body.frequency !== "monthly" && form.due_month) {
        body.due_month = parseInt(form.due_month, 10);
      }
      if (editingId) {
        await api.put(`/bills/${editingId}`, body);
        toast.success("Bill updated");
      } else {
        await api.post("/bills", body);
        toast.success("Bill added");
      }
      setOpen(false);
      setForm(defaultForm);
      setEditingId(null);
      load();
    } catch { toast.error("Couldn't save bill"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? This won't remove past transactions.`)) return;
    try {
      await api.delete(`/bills/${b.id}`);
      toast.success("Bill deleted");
      load();
    } catch { toast.error("Couldn't delete bill"); }
  };

  const openPayDialog = (b) => {
    const remaining = b.paid_this_month?.remaining ?? b.amount;
    setPayBill(b);
    setPayAmount(String(remaining > 0 ? remaining : b.amount));
  };
  const submitPayment = async () => {
    if (!payBill) return;
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    try {
      const r = await api.post(`/bills/${payBill.id}/mark-paid`, { amount: amt });
      const b = r.data.bill;
      const remaining = b.paid_this_month?.remaining || 0;
      const cw = cycleWord(payBill.frequency || "monthly");
      const msg = b.paid_this_month?.is_paid
        ? `${payBill.name} — ${cw} covered ✓`
        : `Logged ${amt.toFixed(2)} · ${remaining.toFixed(2)} still owed this ${cw}`;
      toast.success(msg, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/bills/${payBill.id}/mark-paid/${r.data.entry.month}?entry_id=${r.data.entry.id}`);
              toast.success("Undone");
              load();
            } catch { toast.error("Couldn't undo"); }
          },
        },
      });
      setPayBill(null);
      setPayAmount("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log payment");
    }
  };

  const unmarkPaid = async (bill, month) => {
    try {
      await api.delete(`/bills/${bill.id}/mark-paid/${month}`);
      toast.success("Payment removed");
      // Reload data and refresh open dialog
      const r = await api.get("/bills");
      setData(r.data);
      const fresh = r.data.bills.find(x => x.id === bill.id);
      if (fresh) setHistoryBill(fresh);
    } catch { toast.error("Couldn't remove payment"); }
  };

  const totals = data.totals || {};
  const overallPct = totals.total_amount > 0
    ? Math.min(100, Math.round((totals.paid_this_month / totals.total_amount) * 100))
    : 0;
  const allPaid = totals.count > 0 && totals.bills_paid === totals.count;
  // If every bill shares the same frequency, use that word ("month"/"quarter"/"year") in the summary strip.
  const uniqueFreqs = new Set((data.bills || []).map(b => b.frequency || "monthly"));
  const aggregateCycle = uniqueFreqs.size === 1 ? cycleWord([...uniqueFreqs][0]) : "cycle";

  // Sort visible bills. "default" is soonest-due first (uses
  // `next_due.days_until` from the API; nulls sink to the bottom);
  // "amount_desc" shows biggest bills first, "name_asc" is A→Z.
  const sortedBills = React.useMemo(() => {
    const arr = [...(data.bills || [])];
    if (sortBy === "amount_desc") {
      arr.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    } else if (sortBy === "name_asc") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    } else {
      // default: soonest due-date first; nulls last; tiebreak by higher amount
      arr.sort((a, b) => {
        const da = a?.next_due?.days_until;
        const db = b?.next_due?.days_until;
        const aHas = typeof da === "number";
        const bHas = typeof db === "number";
        if (aHas && bHas && da !== db) return da - db;
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        return (b.amount || 0) - (a.amount || 0);
      });
    }
    return arr;
  }, [data.bills, sortBy]);

  return (
    <Shell>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" data-testid="bills-title">
            Bills
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Track when every utility is due and when you've paid it this cycle
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-full bg-spidey-red text-white hover:opacity-90 shrink-0"
          data-testid="bill-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add bill
        </Button>
      </div>

      {/* Summary strip */}
      {(totals.count || 0) > 0 && (
        <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6" data-testid="bills-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
                Paid this {aggregateCycle}
              </div>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span className="font-display text-2xl sm:text-3xl font-bold tabular text-slate-900 dark:text-slate-100">
                  {fmtMoney(totals.paid_this_month || 0, cur)}
                </span>
                <span className="text-sm text-slate-400 dark:text-slate-500 font-medium">
                  of {fmtMoney(totals.total_amount || 0, cur)}
                </span>
              </div>
              <div className="mt-2 text-xs sm:text-sm">
                {allPaid ? (
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                    ✅ Every bill covered this {aggregateCycle} — nice work
                  </span>
                ) : (
                  <span className="text-slate-600 dark:text-slate-400">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {totals.bills_paid || 0}
                    </span>
                    {" of "}
                    <span className="font-semibold">{totals.count}</span>
                    {" paid · "}
                    <span className="tabular font-semibold text-rose-600 dark:text-rose-400">
                      {fmtMoney(totals.remaining_this_month || 0, cur)}
                    </span>
                    {" still owed"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                allPaid ? "bg-emerald-500" : overallPct >= 75 ? "bg-amber-500" : "bg-spidey-red"
              }`}
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Grid */}
      {(data.bills || []).length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-3xl p-12 text-center">
          <Zap className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto" />
          <div className="mt-3 font-display text-lg font-semibold text-slate-700 dark:text-slate-200">
            No bills tracked yet
          </div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Add your first utility to see due dates and paid status at a glance.
          </div>
          <Button
            onClick={openCreate}
            className="mt-5 rounded-full bg-spidey-red text-white hover:opacity-90"
            data-testid="bill-empty-add"
          >
            <Plus className="w-4 h-4 mr-1" /> Add bill
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
              {sortedBills.length} bill{sortedBills.length === 1 ? "" : "s"}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Sort by</span>
              <Select value={sortBy} onValueChange={setAndSaveSort}>
                <SelectTrigger data-testid="bills-sort-select" className="rounded-full w-[190px] h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="bills-sort-default">Next due (default)</SelectItem>
                  <SelectItem value="amount_desc" data-testid="bills-sort-amount">Amount · high to low</SelectItem>
                  <SelectItem value="name_asc" data-testid="bills-sort-name">Name · A to Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {sortedBills.map(b => {
            const meta = CATEGORY_META[b.category] || CATEGORY_META.other;
            const CatIcon = meta.Icon;
            const isPaid = b.paid_this_month?.is_paid;
            const nd = b.next_due;
            const chipStatus = nd?.status;
            return (
              <div
                key={b.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6"
                data-testid={`bill-card-${b.id}`}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
                      <CatIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 truncate" data-testid={`bill-name-${b.id}`}>
                        {b.name}
                      </div>
                      <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5 flex-wrap">
                        <span>{meta.label}</span>
                        {b.frequency && b.frequency !== "monthly" && (
                          <>
                            <span className="opacity-40">·</span>
                            <span
                              data-testid={`bill-freq-badge-${b.id}`}
                              className="px-1.5 py-[1px] rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 tracking-normal"
                            >
                              {b.frequency === "quarterly" ? "Quarterly" : "Annually"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(b)}
                      className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
                      aria-label={`Edit ${b.name}`}
                      data-testid={`bill-edit-${b.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(b)}
                      className="p-2 rounded-full hover:bg-rose-50 dark:hover:bg-rose-500/10 text-rose-500 dark:text-rose-400"
                      aria-label={`Delete ${b.name}`}
                      data-testid={`bill-delete-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="font-display text-3xl font-bold tabular text-slate-900 dark:text-slate-100" data-testid={`bill-amount-${b.id}`}>
                    {fmtMoney(b.amount, cur)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {b.frequency === "quarterly" ? "/quarter" : b.frequency === "annually" ? "/year" : "/mo"}
                  </div>
                </div>
                {b.notes && (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate">{b.notes}</div>
                )}

                {/* Partial payment progress (only when something is paid but not fully covered) */}
                {(b.paid_this_month?.amount || 0) > 0 && !isPaid && (
                  <div className="mt-3" data-testid={`bill-progress-${b.id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 dark:text-slate-300 font-semibold">
                        <span className="tabular">{fmtMoney(b.paid_this_month.amount, cur)}</span>
                        <span className="text-slate-400 dark:text-slate-500 font-medium"> paid · </span>
                        <span className="tabular text-rose-600 dark:text-rose-400">{fmtMoney(b.paid_this_month.remaining, cur)}</span>
                        <span className="text-slate-500 dark:text-slate-400 font-medium"> left</span>
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 tabular">
                        {b.paid_this_month.payment_count} payment{b.paid_this_month.payment_count !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-amber-500"
                        style={{ width: `${Math.min(100, Math.round((b.paid_this_month.amount / b.amount) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Due-date / Paid chip */}
                {nd && (
                  <div
                    data-testid={`bill-due-chip-${b.id}`}
                    className={`mt-3 flex items-center gap-2 rounded-2xl px-3 py-2 text-xs ${
                      chipStatus === "paid"
                        ? "bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                        : chipStatus === "urgent"
                        ? "bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300"
                        : chipStatus === "soon"
                        ? "bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300"
                        : "bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <span className="text-sm">
                      {chipStatus === "paid" ? "✅" : chipStatus === "urgent" ? "🚨" : chipStatus === "soon" ? "⏰" : "📅"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">
                        {chipStatus === "paid"
                          ? `Paid ${new Date(b.paid_this_month.paid_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${fmtMoney(b.paid_this_month.amount, cur)}`
                          : nd.days_until === 0
                          ? `Due today — ${fmtMoney(b.paid_this_month?.remaining ?? b.amount, cur)}`
                          : nd.days_until === 1
                          ? `Due tomorrow — ${fmtMoney(b.paid_this_month?.remaining ?? b.amount, cur)}`
                          : `Due in ${nd.days_until} days — ${fmtMoney(b.paid_this_month?.remaining ?? b.amount, cur)}`}
                      </div>
                      <div className="text-[10px] opacity-80">
                        {new Date(nd.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        {" · "}
                        {chipStatus === "paid" ? "next due date" : "pay this to stay current"}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {isPaid ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => unmarkPaid(b, currentMonth())}
                      className="rounded-full text-xs"
                      data-testid={`bill-unmark-${b.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Undo paid
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => openPayDialog(b)}
                      className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 text-xs"
                      data-testid={`bill-mark-paid-${b.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      {(b.paid_this_month?.amount || 0) > 0 ? "Log another payment" : "Log payment"}
                    </Button>
                  )}
                  {(b.paid_history || []).length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryBill(b)}
                      className="rounded-full text-xs text-slate-500 dark:text-slate-400"
                      data-testid={`bill-history-${b.id}`}
                    >
                      View {b.paid_history.length} past payment{b.paid_history.length !== 1 ? "s" : ""} →
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingId ? "Edit bill" : "Add a new bill"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                Category
              </label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {Object.entries(CATEGORY_META).map(([k, m]) => {
                  const Icon = m.Icon;
                  const active = form.category === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, category: k }))}
                      className={`flex flex-col items-center gap-1 h-16 rounded-2xl border text-xs font-semibold transition-colors ${
                        active
                          ? "border-spidey-red bg-spidey-red text-white"
                          : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                      data-testid={`bill-cat-${k}`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Name</label>
              <Input
                placeholder="Con Edison, Verizon, PG&E..."
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="rounded-full mt-1.5"
                data-testid="bill-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="120.00"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="rounded-full mt-1.5"
                  data-testid="bill-amount"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                  Due day
                </label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="1 – 31"
                  value={form.due_day_of_month}
                  onChange={e => setForm(f => ({ ...f, due_day_of_month: e.target.value }))}
                  className="rounded-full mt-1.5"
                  data-testid="bill-due-day"
                />
              </div>
            </div>

            {/* Frequency picker (monthly / quarterly / annually) */}
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                Bills every
              </label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {Object.entries(FREQ_LABELS).map(([k, label]) => {
                  const active = form.frequency === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, frequency: k, due_month: k === "monthly" ? "" : (f.due_month || String(new Date().getMonth() + 1)) }))}
                      className={`h-10 rounded-full text-xs font-semibold transition-colors ${
                        active
                          ? "bg-spidey-red text-white"
                          : "border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                      data-testid={`bill-freq-${k}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {form.frequency !== "monthly" && (
                <div className="mt-3">
                  <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                    {form.frequency === "quarterly" ? "Starting month (repeats every 3)" : "Due month"}
                  </label>
                  <Select
                    value={form.due_month}
                    onValueChange={v => setForm(f => ({ ...f, due_month: v }))}
                  >
                    <SelectTrigger data-testid="bill-due-month" className="rounded-full mt-1.5">
                      <SelectValue placeholder="Pick a month" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((m, i) => (
                        <SelectItem key={m} value={String(i + 1)} data-testid={`bill-due-month-${i + 1}`}>
                          {form.frequency === "quarterly"
                            ? `${m} → then every 3 months`
                            : `${m} · once a year`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                Notes <span className="normal-case font-medium">(optional)</span>
              </label>
              <Input
                placeholder="Account number, autopay reminder..."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="rounded-full mt-1.5"
                data-testid="bill-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="bill-cancel">Cancel</Button>
            <Button
              onClick={save}
              className="rounded-full bg-spidey-red text-white hover:opacity-90"
              data-testid="bill-save"
            >
              {editingId ? "Save" : "Add bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log payment (partial or full) dialog */}
      <Dialog open={!!payBill} onOpenChange={(v) => { if (!v) { setPayBill(null); setPayAmount(""); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="bill-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Log payment · {payBill?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-800 p-3 text-xs">
              <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                <span>Bill amount</span>
                <span className="tabular font-semibold text-slate-800 dark:text-slate-200">{fmtMoney(payBill?.amount || 0, cur)}</span>
              </div>
              {(payBill?.paid_this_month?.amount || 0) > 0 && (
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mt-1">
                  <span>Already paid this {cycleWord(payBill?.frequency || "monthly")}</span>
                  <span className="tabular font-semibold text-emerald-600 dark:text-emerald-400">{fmtMoney(payBill.paid_this_month.amount, cur)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mt-1">
                <span>Remaining</span>
                <span className="tabular font-bold text-rose-600 dark:text-rose-400">{fmtMoney(payBill?.paid_this_month?.remaining ?? payBill?.amount ?? 0, cur)}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                Payment amount
              </label>
              <Input
                type="number"
                step="0.01"
                value={payAmount}
                onChange={e => setPayAmount(e.target.value)}
                autoFocus
                className="rounded-full mt-1.5"
                data-testid="bill-pay-amount"
              />
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPayAmount(String(payBill?.paid_this_month?.remaining ?? payBill?.amount ?? 0))}
                  className="rounded-full text-xs"
                  data-testid="bill-pay-full"
                >
                  Full remaining
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPayAmount(String(((payBill?.paid_this_month?.remaining ?? payBill?.amount ?? 0) / 2).toFixed(2)))}
                  className="rounded-full text-xs"
                  data-testid="bill-pay-half"
                >
                  Half
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPayBill(null); setPayAmount(""); }} className="rounded-full" data-testid="bill-pay-cancel">Cancel</Button>
            <Button
              onClick={submitPayment}
              className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="bill-pay-submit"
            >
              Log payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Past payments history dialog */}
      <Dialog open={!!historyBill} onOpenChange={(v) => !v && setHistoryBill(null)}>
        <DialogContent className="sm:max-w-md" data-testid="bill-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {historyBill?.name} — payment history
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {[...(historyBill?.paid_history || [])].sort((a, b) => (b.month || "").localeCompare(a.month || "")).map(h => (
              <div key={h.month} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-2xl px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {new Date(h.month + "-01").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Paid {new Date(h.paid_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular font-bold text-slate-800 dark:text-slate-200">
                    {fmtMoney(h.amount, cur)}
                  </span>
                  <button
                    onClick={() => unmarkPaid(historyBill, h.month)}
                    className="p-1.5 rounded-full text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-500/10"
                    aria-label={`Undo payment for ${h.month}`}
                    data-testid={`bill-history-undo-${h.month}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {(historyBill?.paid_history || []).length === 0 && (
              <div className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
                No payments recorded yet.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryBill(null)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
